/**
 * Subscription Section
 * Sub-component of PreferencesModal for Stripe upgrade, manage subscription, and tier selection.
 */
import { useState, useEffect } from 'react';
import { api, PricingPlan } from '../../services/api';
import { Loader2, CreditCard, AlertCircle } from 'lucide-react';
import type { UserPreferences } from '../../types';
import { StripeCheckoutModal } from '../StripeCheckoutModal';

export interface SubscriptionSectionProps {
  preferences: UserPreferences;
  onPreferencesReload: () => void;
}

export function SubscriptionSection({ preferences, onPreferencesReload }: SubscriptionSectionProps) {
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutDefaultTier, setCheckoutDefaultTier] = useState<'standard' | 'snooze'>('standard');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [standardPlan, setStandardPlan] = useState<PricingPlan | null>(null);
  const [hibernatePlan, setHibernatePlan] = useState<PricingPlan | null>(null);

  useEffect(() => {
    api.getPublicPricing().then(({ pricing }) => {
      setStandardPlan(pricing?.plans?.standard || null);
      setHibernatePlan(pricing?.plans?.hibernate || null);
    }).catch((err) => {
      console.error('Failed to load pricing for subscription section', err);
    });
  }, []);

  // Status labels for display
  const statusLabels: Record<string, { label: string; className: string }> = {
    'trial': { label: 'Trial', className: 'bg-yellow-100 text-yellow-700' },
    'active': { label: 'Active', className: 'bg-green-100 text-green-700' },
    'hibernating': { label: 'Hibernating', className: 'bg-orange-100 text-orange-700' },
    'cancelled': { label: 'Cancelled', className: 'bg-gray-100 text-gray-700' },
    'expired': { label: 'Expired', className: 'bg-red-100 text-red-700' },
  };

  // Plan labels for display
  const planLabels: Record<string, { label: string; className: string }> = {
    'free': { label: 'Free (Friends & Family)', className: 'bg-green-100 text-green-700' },
    'consumer': { label: 'Consumer', className: 'bg-blue-100 text-blue-700' },
    'enterprise': { label: 'Enterprise', className: 'bg-indigo-100 text-indigo-700' },
  };

  const userStatus = preferences.status;
  const userPlan = preferences.plan;
  const isFreePlan = userPlan === 'free';

  function handleUpgrade(tier: 'standard' | 'snooze' = 'standard') {
    setCheckoutDefaultTier(tier);
    setShowCheckoutModal(true);
  }

  function handleCheckoutSuccess() {
    setShowCheckoutModal(false);
    // Reload preferences to reflect new subscription status
    onPreferencesReload();
  }

  async function handleManageSubscription() {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const { portalUrl } = await api.getCustomerPortalUrl();
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to get customer portal URL:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      setPortalError(axiosError?.response?.data?.message || 'Failed to open subscription management. Please try again.');
      setPortalLoading(false);
    }
  }

  return (
    <>
      <div className="p-3 space-y-3">
        {/* Login email (read-only — for verification, not editable) */}
        {preferences.email && (
          <div className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700 flex-shrink-0">Login email</span>
            <span className="text-sm font-medium text-gray-900 truncate" title={preferences.email}>
              {preferences.email}
            </span>
          </div>
        )}

        {/* Status */}
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-700">Status</span>
          <span data-help-target="mainapp.user.settings.subscription.status" className={`px-3 py-1 rounded-full text-sm font-medium ${
            (userStatus && statusLabels[userStatus]?.className) || 'bg-gray-100 text-gray-700'
          }`}>
            {(userStatus && statusLabels[userStatus]?.label) || userStatus || 'Unknown'}
          </span>
        </div>

        {/* Plan */}
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-700">Plan</span>
          <span data-help-target="mainapp.user.settings.subscription.plan" className={`px-3 py-1 rounded-full text-sm font-medium ${
            (userPlan && planLabels[userPlan]?.className) || 'bg-gray-100 text-gray-700'
          }`}>
            {(userPlan && planLabels[userPlan]?.label) || userPlan || 'Unknown'}
          </span>
        </div>

        {/* Hibernating Notice */}
        {userStatus === 'hibernating' && (
          <div className="flex items-center p-2 bg-orange-50 text-orange-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            Subscription hibernating - you can edit but AI features are disabled. Limited to 3 jobs/month.
          </div>
        )}

        {/* Expired Notice */}
        {userStatus === 'expired' && (
          <div className="flex items-center p-2 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            Subscription expired - limited to 3 jobs and 1 resume. Upgrade to continue.
          </div>
        )}

        {/* Cancelled Notice */}
        {userStatus === 'cancelled' && (
          <div className="flex items-center p-2 bg-gray-50 text-gray-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            Subscription cancelled. Resubscribe to regain full access.
          </div>
        )}

        {/* Trial Notice */}
        {userStatus === 'trial' && (
          <div className="flex items-center p-2 bg-yellow-50 text-yellow-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            Trial: Limited to 1 resume and 3 jobs. Upgrade anytime.
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-2 space-y-2">
          {!isFreePlan && (userStatus === 'trial' || userStatus === 'expired' || userStatus === 'cancelled') && (
            <button
              onClick={() => handleUpgrade('standard')}
              data-help-target="mainapp.user.settings.subscription.upgrade"
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Upgrade to Paid
            </button>
          )}
          {userStatus === 'active' && userPlan === 'consumer' && (
            <>
              {hibernatePlan && (
                <button
                  onClick={() => handleUpgrade('snooze')}
                  className="w-full px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 flex items-center justify-center"
                >
                  Switch to Hibernate ({hibernatePlan.display_price}{hibernatePlan.period_label})
                </button>
              )}
              {portalError && (
                <div className="flex items-center p-2 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {portalError}
                </div>
              )}
              <button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center"
              >
                {portalLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {portalLoading ? 'Redirecting...' : 'Manage Subscription'}
              </button>
            </>
          )}
          {userStatus === 'hibernating' && userPlan === 'consumer' && standardPlan && (
            <button
              onClick={() => handleUpgrade('standard')}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Reactivate to Standard ({standardPlan.display_price}{standardPlan.period_label})
            </button>
          )}
        </div>
      </div>

      {/* Stripe Checkout Modal */}
      <StripeCheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        onSuccess={handleCheckoutSuccess}
        defaultTier={checkoutDefaultTier}
      />
    </>
  );
}
